/**
 * The Data tab's shared pieces: a modal dialog (focus kept inside, Escape closes, focus goes back), a confirmation,
 * the loading / empty / error states, the read-only badge.
 */
import React, { useEffect, useId, useRef } from 'react';
import { createPortal } from 'react-dom';
import PropTypes from 'prop-types';
import { CircleNotch, Lock, Warning, X } from '@phosphor-icons/react';

const FOCUSABLE = 'button:not([disabled]), [href], input:not([disabled]), select:not([disabled]), textarea:not([disabled]), [tabindex]:not([tabindex="-1"])';

export function DataDialog({ title, subtitle = null, onClose, children, footer = null, wide = false, testId = 'data-dialog' }) {
  const titleId = useId();
  const ref = useRef(null);
  useEffect(() => {
    const before = document.activeElement;
    const node = ref.current;
    const first = node?.querySelector('[data-autofocus]:not(:disabled)') || node?.querySelector(FOCUSABLE);
    (first || node)?.focus();
    return () => { if (before && typeof before.focus === 'function') { before.focus(); } };
  }, []);
  // Escape closes the dialog even when the focus is not inside it (a disabled field, a click on the backdrop's page):
  // only the topmost dialog answers; a key inside the dialog is handled by onKeyDown below.
  const closeRef = useRef(onClose);
  closeRef.current = onClose;
  useEffect(() => {
    const onDocKey = (event) => {
      const node = ref.current;
      if (event.key !== 'Escape' || !node || node.contains(event.target)) { return; }
      const dialogs = document.querySelectorAll('.data-dialog');
      if (dialogs[dialogs.length - 1] !== node) { return; }
      closeRef.current?.();
    };
    document.addEventListener('keydown', onDocKey);
    return () => document.removeEventListener('keydown', onDocKey);
  }, []);
  const onKeyDown = (event) => {
    if (event.key === 'Escape') { event.stopPropagation(); onClose(); return; }
    if (event.key !== 'Tab') { return; }
    const items = [...(ref.current?.querySelectorAll(FOCUSABLE) || [])];
    if (!items.length) { return; }
    const first = items[0];
    const last = items[items.length - 1];
    if (event.shiftKey && document.activeElement === first) { event.preventDefault(); last.focus(); }
    if (!event.shiftKey && document.activeElement === last) { event.preventDefault(); first.focus(); }
  };
  return createPortal(
    <div className="data-dialog-backdrop" onMouseDown={(event) => { if (event.target === event.currentTarget) { onClose(); } }}>
      <section
        aria-labelledby={titleId}
        aria-modal="true"
        className={`data-dialog${wide ? ' is-wide' : ''}`}
        data-testid={testId}
        onKeyDown={onKeyDown}
        ref={ref}
        role="dialog"
        tabIndex={-1}
      >
        <header className="data-dialog-head">
          <div className="data-dialog-titles">
            <h2 id={titleId}>{title}</h2>
            {subtitle && <p>{subtitle}</p>}
          </div>
          <button aria-label="Close" className="data-btn is-icon" onClick={onClose} type="button"><X aria-hidden size={16} /></button>
        </header>
        <div className="data-dialog-body">{children}</div>
        {footer && <footer className="data-dialog-foot">{footer}</footer>}
      </section>
    </div>,
    document.body,
  );
}

DataDialog.propTypes = {
  children: PropTypes.node,
  footer: PropTypes.node,
  onClose: PropTypes.func.isRequired,
  subtitle: PropTypes.node,
  testId: PropTypes.string,
  title: PropTypes.node.isRequired,
  wide: PropTypes.bool,
};

/** "Delete this document?" with a red confirm button; `busy` while it runs, `error` if it failed. */
export function ConfirmDialog({ title, children = null, confirmLabel = 'Delete', danger = true, busy = false, error = null, onConfirm, onClose }) {
  return (
    <DataDialog
      footer={(
        <>
          <span className="data-grow" />
          <button className="data-btn" onClick={onClose} type="button">Cancel</button>
          <button className={`data-btn ${danger ? 'is-danger-solid' : 'is-primary'}`} data-autofocus disabled={busy} onClick={onConfirm} type="button">
            {busy ? 'Working…' : confirmLabel}
          </button>
        </>
      )}
      onClose={onClose}
      testId="data-confirm"
      title={title}
    >
      {children}
      {error && <p className="data-error-text" role="alert">{error}</p>}
    </DataDialog>
  );
}

ConfirmDialog.propTypes = {
  busy: PropTypes.bool,
  children: PropTypes.node,
  confirmLabel: PropTypes.string,
  danger: PropTypes.bool,
  error: PropTypes.string,
  onClose: PropTypes.func.isRequired,
  onConfirm: PropTypes.func.isRequired,
  title: PropTypes.node.isRequired,
};

export function Loading({ label = 'Loading…' }) {
  return (
    <div className="data-state" role="status">
      <CircleNotch aria-hidden className="data-spin" size={18} />
      <span>{label}</span>
    </div>
  );
}

Loading.propTypes = { label: PropTypes.string };

export function EmptyState({ title, children = null, action = null }) {
  return (
    <div className="data-state is-empty">
      <strong>{title}</strong>
      {children && <p>{children}</p>}
      {action}
    </div>
  );
}

EmptyState.propTypes = { action: PropTypes.node, children: PropTypes.node, title: PropTypes.node.isRequired };

export function ErrorState({ error, onRetry = null }) {
  const message = typeof error === 'string' ? error : error?.message || 'Something went wrong';
  return (
    <div className="data-state is-error" role="alert">
      <Warning aria-hidden size={18} />
      <span>{message}</span>
      {onRetry && <button className="data-btn" onClick={onRetry} type="button">Try again</button>}
    </div>
  );
}

ErrorState.propTypes = { error: PropTypes.oneOfType([PropTypes.string, PropTypes.object]), onRetry: PropTypes.func };

/** The "Read-only" pill: why, in its title. */
export function ReadOnlyBadge({ reason = 'You can look but not change anything here', label = 'Read-only' }) {
  return (
    <span className="data-pill" data-testid="data-readonly" title={reason}>
      <Lock aria-hidden size={12} />
      {label}
    </span>
  );
}

ReadOnlyBadge.propTypes = { label: PropTypes.string, reason: PropTypes.string };

/** Saves a Blob as a file. */
export function saveBlob(blob, name) {
  const url = URL.createObjectURL(blob);
  const link = document.createElement('a');
  link.href = url;
  link.download = name || 'download';
  document.body.appendChild(link);
  link.click();
  link.remove();
  setTimeout(() => URL.revokeObjectURL(url), 1000);
}

/** The text of an error for a person: the server's own words first. */
export const errorText = (error) => error?.body?.error || error?.message || String(error || 'Something went wrong');
